import type { LabelColor } from 'src/components/label';

/**
 * Single source of truth for attendance status display. The three attendance
 * tables (branch, employee, generic row) previously each hardcoded their own
 * getStatusColor/getStatusLabel with diverging values — consolidated here.
 *
 * Keys cover the backend `AttendanceStatus` enum (ACTIVE, COMPLETED, ON_TIME,
 * LATE, EARLY_LEAVE, ABSENT, ON_LEAVE, HALF_DAY) plus a few legacy aliases the
 * old components used, so nothing regresses.
 */
export const ATTENDANCE_STATUS_LABELS_TR: Record<string, string> = {
  ACTIVE: 'Devam Ediyor',
  COMPLETED: 'Tamamlandı',
  ON_TIME: 'Zamanında',
  LATE: 'Geç Geldi',
  EARLY_LEAVE: 'Erken Ayrıldı',
  ABSENT: 'Gelmedi',
  ON_LEAVE: 'İzinli',
  HALF_DAY: 'Yarım Gün',
  // legacy aliases (older payloads / components)
  PRESENT: 'Mevcut',
  OVERTIME: 'Mesai',
  PENDING: 'Beklemede',
  EARLY_DEPARTURE: 'Erken Çıkış',
};

export const ATTENDANCE_STATUS_COLORS: Record<string, LabelColor> = {
  ACTIVE: 'info',
  COMPLETED: 'success',
  ON_TIME: 'success',
  LATE: 'warning',
  EARLY_LEAVE: 'info',
  ABSENT: 'error',
  ON_LEAVE: 'warning',
  HALF_DAY: 'warning',
  // legacy aliases
  PRESENT: 'success',
  OVERTIME: 'secondary',
  PENDING: 'warning',
  EARLY_DEPARTURE: 'info',
};

export const AUTO_CHECKOUT_LABEL_TR = 'Otomatik Çıkış';
export const AUTO_CHECKOUT_TOOLTIP_TR =
  'Çalışan çıkış yapmadığı için sistem şube kapanış saatinde otomatik kapattı.';

export function attendanceStatusLabel(status?: string | null): string {
  if (!status) return '-';
  return ATTENDANCE_STATUS_LABELS_TR[status.toUpperCase()] ?? status;
}

export function attendanceStatusColor(status?: string | null): LabelColor {
  if (!status) return 'default';
  return ATTENDANCE_STATUS_COLORS[status.toUpperCase()] ?? 'default';
}

/** An attendance row is "ongoing" (open shift) when it's ACTIVE or has no check-out. */
export function isOngoing(record: { status?: string | null; checkOutTime?: string | null }): boolean {
  return record?.status?.toUpperCase() === 'ACTIVE' || !record?.checkOutTime;
}

/** Work duration in minutes from real timestamps (the stored backend duration is unreliable). */
export function durationMinutesFromTimestamps(checkIn?: string | null, checkOut?: string | null): number | null {
  if (!checkIn || !checkOut) return null;
  const a = new Date(checkIn).getTime();
  const b = new Date(checkOut).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null;
  return Math.floor((b - a) / 60000);
}

/** Turkish short duration: 547 → "9s 7dk". */
export function formatDurationTr(minutes?: number | null): string {
  if (minutes == null || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}s ${m}dk`;
  if (h > 0) return `${h}s`;
  return `${m}dk`;
}
